<template>
    <div class="creditors-section">
        <h2 class="creditors-title">Lista de Credores</h2>

        <div class="creditors-search mb-4">
            <v-row dense align="end">
                <v-col cols="12" md="6">
                    <span class="text-body-2 grey--text text--darken-1">Busque pelo nome do credor:</span>
                    <div class="d-flex align-center mt-2">
                        <v-text-field
                            v-model="searchName"
                            dense
                            outlined
                            hide-details
                            placeholder="Digite ao menos 3 letras"
                            @keyup.enter="search"
                            class="mr-2"
                        ></v-text-field>
                        <v-btn color="primary" outlined elevation="0" @click="search" :loading="loading">
                            Buscar
                        </v-btn>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <span class="text-body-2 grey--text text--darken-1">Versão da lista:</span>
                    <v-select
                        v-model="selectedVersion"
                        :items="versionOptions"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        class="mt-2"
                        @change="filterVersion"
                    ></v-select>
                </v-col>
            </v-row>
        </div>

        <v-simple-table class="creditors-table" v-if="creditors.length > 0 || loading">
            <thead>
                <tr>
                    <th>Nome</th>
                    <th>Classe</th>
                    <th>Valor total</th>
                    <th class="text-right">Linha do tempo</th>
                </tr>
            </thead>
            <tbody>
                <template v-if="loading">
                    <tr v-for="i in 5" :key="'skel-' + i">
                        <td><v-skeleton-loader type="text" width="200"></v-skeleton-loader></td>
                        <td><v-skeleton-loader type="text" width="80"></v-skeleton-loader></td>
                        <td><v-skeleton-loader type="text" width="100"></v-skeleton-loader></td>
                        <td><v-skeleton-loader type="text" width="90"></v-skeleton-loader></td>
                    </tr>
                </template>
                <template v-else>
                    <tr v-for="(item, index) in creditors" :key="index">
                        <td class="font-weight-medium">{{ item.name }}</td>
                        <td>{{ getClassName(item.type) }}</td>
                        <td class="font-weight-medium">R$ {{ formatCurrency(item.amount) }}</td>
                        <td class="text-right">
                            <v-btn small text color="primary" class="creditor-details-btn" @click="openTimeline(item)">
                                <v-icon left small>mdi-timeline-clock-outline</v-icon>Ver detalhes
                            </v-btn>
                        </td>
                    </tr>
                </template>
            </tbody>
        </v-simple-table>

        <div v-else-if="!loading && loadError" class="text-center py-8 grey--text">
            {{ loadError }}
        </div>

        <div v-else-if="!loading && searched" class="text-center py-8 grey--text">
            Nenhum credor encontrado.
        </div>

        <div v-if="paging.lastPage > 1" class="d-flex justify-center align-center mt-4" style="gap: 12px;">
            <v-btn icon small :disabled="paging.page <= 1" @click="changePage(paging.page - 1)">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-body-2 grey--text text--darken-1">
                Página {{ paging.page }} de {{ paging.lastPage }} ({{ paging.total }} credores)
            </span>
            <v-btn icon small :disabled="paging.page >= paging.lastPage" @click="changePage(paging.page + 1)">
                <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
        </div>

        <!-- Linha do tempo do credor: o credito dele em cada versao da lista -->
        <v-dialog v-model="timeline.open" max-width="680" scrollable>
            <v-card class="creditor-timeline">
                <v-card-title class="creditor-timeline__title">
                    <div>
                        <span class="creditor-timeline__eyebrow">Linha do tempo do credor</span>
                        <h3>{{ timeline.name }}</h3>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn icon aria-label="Fechar" @click="timeline.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <div v-if="timeline.loading" class="py-6">
                        <v-skeleton-loader type="list-item-two-line@3"></v-skeleton-loader>
                    </div>
                    <div v-else-if="timeline.error" class="text-center py-8 grey--text">
                        Não foi possível carregar a linha do tempo deste credor.
                    </div>
                    <v-timeline v-else :dense="$vuetify.breakpoint.smAndDown" class="creditor-timeline__list">
                        <v-timeline-item v-for="event in timeline.events" :key="event.version" small
                            :color="event.credits.length ? 'primary' : 'grey lighten-1'">
                            <template v-if="!$vuetify.breakpoint.smAndDown" v-slot:opposite>
                                <span class="creditor-timeline__date">{{ event.reference_date ? formatDate(event.reference_date) : '' }}</span>
                            </template>
                            <div class="creditor-timeline__card" :class="{ 'creditor-timeline__card--empty': !event.credits.length }">
                                <strong v-if="event.reference_date && $vuetify.breakpoint.smAndDown" class="d-block">{{ formatDate(event.reference_date) }}</strong>
                                <span class="creditor-timeline__event-title">{{ event.title }}</span>
                                <div class="mt-1 mb-2">
                                    <v-chip x-small outlined :color="statusColor(event.status)" class="mr-1">{{ statusLabel(event.status) }}</v-chip>
                                    <v-chip v-if="event.is_current" x-small color="primary" text-color="white">Vigente</v-chip>
                                </div>
                                <div v-for="(credit, index) in event.credits" :key="index" class="creditor-timeline__credit">
                                    <span>{{ getClassName(credit.class) }}</span>
                                    <strong>{{ currencySymbol(credit.currency) }} {{ formatCurrency(credit.amount) }}</strong>
                                </div>
                                <span v-if="!event.credits.length" class="text-caption grey--text">O credor não consta nesta versão da lista.</span>
                            </div>
                        </v-timeline-item>
                    </v-timeline>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn color="primary" depressed rounded @click="timeline.open = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import creditorTypes from '@/common/data/creditor-types.data';

export default {
    name: 'CreditorsList',

    props: {
        clientId: { type: [String, Number], required: true },
    },

    data: () => ({
        creditors: [],
        loading: false,
        searched: false,
        loadError: '',
        searchName: '',
        // null = "Atualizada": a versao vigente do caso (currentVersion).
        selectedVersion: null,
        currentVersion: null,
        paging: { page: 1, lastPage: 1, total: 0 },
        timeline: { open: false, loading: false, error: false, name: '', events: [] },
        creditorTypes: creditorTypes,
        versionOptions: [
            { id: null, name: 'Atualizada' },
            { id: 1, name: 'Edital Art. 52' },
            { id: 2, name: 'Edital Art. 7º' },
            { id: 3, name: 'QGC consolidado' },
        ],
    }),

    async mounted() {
        this.loadCreditorClasses();
        // A versao vigente precisa vir antes da primeira busca ("Atualizada").
        await this.loadVersions();
        this.load();
    },

    methods: {
        async loadCreditorClasses() {
            if (!this.clientId) return;

            try {
                const result = await axios.get(`/api/clients/${this.clientId}/creditor-classes`, {
                    params: { include_inactive: true }
                });

                const data = (result.data.content && result.data.content.data) || [];

                if (!data.length) return;

                // Inclui ocultas para que credores ja classificados nelas
                // continuem exibindo o nome da classe.
                this.creditorTypes = data.map((item) => ({
                    id: item.legacy_class_id,
                    name: item.name,
                }));
            } catch (error) {
                // Mantem as classes padrao importadas do arquivo local.
            }
        },

        // QGCs cadastrados do caso; mantem a lista fixa se o caso nao tiver cadastro.
        async loadVersions() {
            if (!this.clientId) return;
            try {
                const result = await axios.get(`/api/clients/${this.clientId}/qgc-versions`);
                const content = result.data.content || {};
                const versions = content.versions || [];
                this.currentVersion = content.current_version || null;
                if (versions.length) {
                    this.versionOptions = [{ id: null, name: 'Atualizada' }].concat(
                        versions.map((item) => ({ id: item.version, name: `${item.title}${item.is_current ? ' (vigente)' : ''}` }))
                    );
                }
            } catch (error) {
                // mantem as opcoes padrao
            }
        },

        async load(page = 1) {
            this.loading = true;
            this.searched = true;
            this.loadError = '';

            try {
                const params = { page, limit: 15 };

                // "Atualizada" busca a versao vigente; sem versoes cadastradas, segue sem filtro.
                const version = this.selectedVersion !== null ? this.selectedVersion : this.currentVersion;
                if (version !== null) {
                    params.version = version;
                }

                if (this.searchName && this.searchName.length >= 3) {
                    params.search = JSON.stringify({ name: this.searchName });
                }

                const result = await axios.get(`/api/clients/creditors/${this.clientId}`, { params });
                this.creditors = result.data.content.data;
                this.paging = {
                    page: result.data.content.paging.page,
                    lastPage: result.data.content.paging.lastPage,
                    total: result.data.content.paging.total,
                };
            } catch (error) {
                // Sem zerar a paginacao, a tela mostraria o total da busca anterior
                // ao lado de "Nenhum credor encontrado" (ex.: versao ainda nao publicada).
                this.creditors = [];
                this.paging = { page: 1, lastPage: 1, total: 0 };
                const data = error.response && error.response.data;
                const message = data && ((data.error && data.error.message) || (data.content && data.content.error && data.content.error.message));
                this.loadError = message || 'Não foi possível carregar a lista de credores. Tente novamente.';
            } finally {
                this.loading = false;
            }
        },

        // Abre a linha do tempo do credor (credito em cada versao da lista).
        async openTimeline(item) {
            this.timeline = { open: true, loading: true, error: false, name: item.name, events: [] };
            try {
                const result = await axios.get('/api/clients/' + this.clientId + '/creditors/' + item.id + '/timeline');
                const content = result.data.content || {};
                this.timeline.events = content.events || [];
            } catch (error) {
                this.timeline.error = true;
            } finally {
                this.timeline.loading = false;
            }
        },

        statusLabel(status) {
            const labels = { included: 'Incluído na lista', changed: 'Crédito alterado', unchanged: 'Sem alteração', removed: 'Não consta mais', absent: 'Não consta' };
            return labels[status] || '';
        },

        statusColor(status) {
            if (status === 'changed') return 'orange darken-2';
            if (status === 'included' || status === 'unchanged') return 'primary';
            return 'grey';
        },

        currencySymbol(currency) {
            return Number(currency) === 2 ? 'US$' : (Number(currency) === 3 ? '€' : 'R$');
        },

        formatDate(value) {
            const parts = String(value || '').slice(0, 10).split('-');
            return parts.length === 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : '';
        },

        search() {
            this.load(1);
        },

        filterVersion() {
            this.load(1);
        },

        changePage(page) {
            this.load(page);
        },

        getClassName(type) {
            const found = this.creditorTypes.find(c => Number(c.id) === Number(type));
            return found ? found.name : '-';
        },

        formatRegistration(reg) {
            if (!reg) return '-';
            const clean = reg.replace(/\D/g, '');
            if (clean.length === 11) {
                return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            }
            if (clean.length === 14) {
                return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            }
            return reg;
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return '0,00';
            return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.creditors-section {
    margin-top: 40px;
    margin-bottom: 40px;
}

.creditors-title {
    font-size: 24px;
    font-weight: 700;
    color: #180032;
    margin-bottom: 16px;
}

.creditors-table {
    border: 1px solid #EFEFEF;
    border-radius: 8px;
    overflow: hidden;
}

.creditors-table th {
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #525252 !important;
    background-color: #F7F7F7 !important;
    border-bottom: 1px solid #EFEFEF !important;
    height: 44px !important;
}

.creditors-table td {
    font-size: 14px !important;
    color: #333 !important;
    border-bottom: 1px solid #F7F7F7 !important;
    height: 48px !important;
}

.creditor-details-btn {
    text-transform: none;
    letter-spacing: 0;
}

.creditor-timeline__title {
    align-items: flex-start;
    flex-wrap: nowrap;
}

.creditor-timeline__title h3 {
    font-size: 18px;
    font-weight: 700;
    color: #180032;
    word-break: break-word;
}

.creditor-timeline__eyebrow {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6B7280;
}

.creditor-timeline__date {
    font-weight: 700;
    color: #180032;
}

.creditor-timeline__card {
    background: #F5F5F5;
    border-radius: 6px;
    padding: 12px 14px;
}

.creditor-timeline__card--empty {
    background: #FAFAFA;
}

.creditor-timeline__event-title {
    display: block;
    font-weight: 600;
    text-transform: uppercase;
    color: #333;
}

.creditor-timeline__credit {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    padding: 2px 0;
}
</style>
